<template>
  <div>
    <!-- Scholarship -->
    <b-card no-body v-if="isHeadOffice">
      <b-card-header
        class="p-1 d-flex align-items-center justify-content-between flex-wrap"
      >
        <div class="d-flex align-items-center flex-wrap">
          <h4 class="mb-0 mr-1">{{ $t("Scholarship Statistics") }}</h4>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="
              scholarshipLoading || scholarshipPrinting || scholarshipExporting
            "
            @click="$bvModal.show(filterModalId)"
          >
            <feather-icon icon="FilterIcon" class="mr-50" />
            {{ $t("Filter") }}
          </b-button>
        </div>
        <div class="d-flex align-items-center flex-wrap mt-25 mt-sm-0">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="
              scholarshipLoading || scholarshipPrinting || scholarshipExporting
            "
            @click="printScholarshipSummary(false)"
          >
            <b-spinner
              v-if="scholarshipPrinting"
              class="mr-50"
              variant="primary"
              small
            />
            <feather-icon v-else icon="PrinterIcon" size="16" class="mr-50" />
            {{ $t("Print") }}
          </b-button>
          <b-button
            variant="outline-success"
            size="sm"
            class="ml-50"
            :disabled="
              scholarshipLoading || scholarshipPrinting || scholarshipExporting
            "
            @click="printScholarshipSummary(true)"
          >
            <b-spinner
              v-if="scholarshipExporting"
              class="mr-50"
              variant="success"
              small
            />
            <feather-icon v-else icon="DownloadIcon" size="16" class="mr-50" />
            {{ $t("Excel") }}
          </b-button>
        </div>
      </b-card-header>

      <b-card-body class="px-1 pb-1">
        <b-row v-if="scholarshipLoading">
          <b-col md="3" v-for="n in 4" :key="n">
            <div class="scholarship-skeleton-loader"></div>
          </b-col>
        </b-row>

        <b-row v-else class="scholarship-stats-card">
          <!-- Card 1: Total Assigned -->
          <b-col xl="3" md="6" cols="12">
            <div class="scholarship-card-new border-top-primary">
              <div
                class="d-flex justify-content-between align-items-center mb-25"
              >
                <span class="scholarship-card-title">{{
                  $t("Total Assigned")
                }}</span>
                <div class="scholarship-card-icon-container bg-light-primary">
                  <feather-icon icon="UsersIcon" size="20" />
                </div>
              </div>
              <h2
                class="scholarship-card-value font-weight-bolder text-primary"
              >
                {{ scholarshipTotals.assigned }}
              </h2>
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div v-for="(cls, cInd) in assignedStrength" :key="cInd">
                  <span>{{ cls.className }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    cls.strength
                  }}</span>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <div>
                    {{ $t("Male") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.assignedMalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.assignedMale }})
                  </div>
                </div>
                <div class="text-right">
                  <div>
                    {{ $t("Female") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.assignedFemalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.assignedFemale }})
                  </div>
                </div>
              </div>
            </div>
          </b-col>

          <!-- Card 2: Total Accepted -->
          <b-col xl="3" md="6" cols="12">
            <div class="scholarship-card-new border-top-success">
              <div
                class="d-flex justify-content-between align-items-center mb-25"
              >
                <span class="scholarship-card-title">{{
                  $t("Total Accepted")
                }}</span>
                <div class="scholarship-card-icon-container bg-light-success">
                  <feather-icon icon="CheckCircleIcon" size="20" />
                </div>
              </div>
              <h2
                class="scholarship-card-value font-weight-bolder text-success"
              >
                {{ scholarshipTotals.accepted }}
                <span class="font-small-3 text-dark"
                  >({{ scholarshipTotals.acceptedPerc }}%)</span
                >
              </h2>
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div v-for="(cls, cInd) in acceptedStrength" :key="cInd">
                  <span>{{ cls.className }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    cls.acceptedCount
                  }}</span>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <div>
                    {{ $t("Male") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.acceptedMalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.acceptedMale }})
                  </div>
                </div>
                <div class="text-right">
                  <div>
                    {{ $t("Female") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.acceptedFemalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.acceptedFemale }})
                  </div>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <span>{{ $t("App") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.acceptedAppLogin
                  }}</span>
                </div>
                <div class="text-right">
                  <span>{{ $t("Admin") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.acceptedAdminPortal
                  }}</span>
                </div>
              </div>
            </div>
          </b-col>

          <!-- Card 3: Not Accepted -->
          <b-col xl="3" md="6" cols="12">
            <div class="scholarship-card-new border-top-warning">
              <div
                class="d-flex justify-content-between align-items-center mb-25"
              >
                <span class="scholarship-card-title">{{
                  $t("Not Accepted")
                }}</span>
                <div class="scholarship-card-icon-container bg-light-warning">
                  <feather-icon icon="XCircleIcon" size="20" />
                </div>
              </div>
              <h2
                class="scholarship-card-value font-weight-bolder text-warning"
              >
                {{ scholarshipTotals.notAccepted }}
              </h2>
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div v-for="(cls, cInd) in notAcceptedStrength" :key="cInd">
                  <span>{{ cls.className }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    cls.stdCount
                  }}</span>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <div>
                    {{ $t("Male") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.notAcceptedMalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.notAcceptedMale }})
                  </div>
                </div>
                <div class="text-right">
                  <div>
                    {{ $t("Female") }}:
                    <span class="font-weight-bolder text-dark"
                      >{{ genderStats.notAcceptedFemalePerc }}%</span
                    >
                  </div>
                  <div class="font-weight-bolder text-dark">
                    ({{ genderStats.notAcceptedFemale }})
                  </div>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <span>{{ $t("N/A") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    remainingNotAccepted
                  }}</span>
                </div>
                <div class="text-right">
                  <span>{{ $t("Left") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.notAcceptedLeft
                  }}</span>
                </div>
              </div>
            </div>
          </b-col>

          <!-- Card 4: Student Status -->
          <b-col xl="3" md="6" cols="12">
            <div class="scholarship-card-new border-top-info">
              <div
                class="d-flex justify-content-between align-items-center mb-25"
              >
                <span class="scholarship-card-title">{{
                  $t("Student Status")
                }}</span>
                <div class="scholarship-card-icon-container bg-light-info">
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-info"
                  >
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                    <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"></path>
                  </svg>
                </div>
              </div>
              <h2 class="scholarship-card-value font-weight-bolder text-info">
                {{ scholarshipTotals.active + scholarshipTotals.onProbation }}
              </h2>
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <span>{{ $t("Active") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.active
                  }}</span>
                </div>
                <div class="text-right">
                  <span>{{ $t("Probation") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.onProbation
                  }}</span>
                </div>
              </div>
              <hr class="scholarship-card-divider" />
              <div
                class="d-flex justify-content-between text-dark font-small-3"
              >
                <div>
                  <span>{{ $t("Inactive") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">{{
                    scholarshipTotals.inactive
                  }}</span>
                </div>
                <div class="text-right">
                  <span>{{ $t("Dropout Rate") }}:</span>
                  <span class="font-weight-bolder text-dark ml-25">
                    {{
                      (scholarshipTotals.active + scholarshipTotals.onProbation
                        ? (scholarshipTotals.inactive /
                            (scholarshipTotals.active +
                              scholarshipTotals.onProbation)) *
                          100
                        : 0
                      ).toFixed(1)
                    }}%
                  </span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-75 match-height">
          <b-col xl="6" md="12" cols="12">
            <b-card no-body class="mb-1">
              <b-table
                ref="facultyStatsTable"
                class="mb-0 scholarship-card-table"
                :items="classStats"
                :fields="facultyStatsFields"
                :busy="otherLoading"
                responsive
                outlined
                show-empty
              >
                <template #table-busy>
                  <div class="text-center my-1">
                    <b-spinner variant="success" />
                  </div>
                </template>

                <template #cell(section)="{ item }">
                  {{ item.sectionName }}
                </template>

                <template
                  v-for="mid in facultyStatsDistinctClasses"
                  v-slot:[`cell(mdb_${mid})`]="{ item }"
                >
                  <div v-if="getFacultyStatsForClass(item, mid)" class="">
                    <div>
                      {{
                        getFacultyStatsForClass(item, mid)
                          .acceptedRatioInSection
                      }}
                      %
                    </div>
                  </div>
                </template>
              </b-table>
            </b-card>
          </b-col>

          <b-col v-if="!isPcp && !isUtp" xl="6" md="12" cols="12">
              <div class="scholarship-card-new mb-1 py-1 px-1_5">
                <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap">
                  <span class="scholarship-card-title mb-0">{{ $t("Category Summary") }}</span>
                  <div class="d-flex align-items-center font-small-2">
                    <span class="d-flex align-items-center mr-1">
                      <span class="bullet bullet-sm bullet-coaching mr-50"></span>
                      <span class="font-weight-bolder text-coaching">{{ $t("Offered Coaching") }}</span>
                    </span>
                    <span class="d-flex align-items-center mr-1">
                      <span class="bullet bullet-sm bullet-pvt-college mr-50"></span>
                      <span class="font-weight-bolder text-pvt-college">{{ $t("Offered Pvt College") }}</span>
                    </span>
                    <b-spinner v-if="catLoading" small variant="primary" />
                  </div>
                </div>

                <div v-if="!catLoading && (!lastResultSummary || lastResultSummary.length === 0)" class="text-center text-muted my-auto py-2">
                  <small>{{ $t("No data available") }}</small>
                </div>

                <div v-else class="last-result-summary-list">
                  <div
                    v-for="(item, idx) in lastResultSummary"
                    :key="idx"
                    class="last-result-summary-item mb-1"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-50 font-small-3">
                      <div>
                        <span class="badge badge-light-secondary font-weight-bolder mr-50 px-50 py-25">
                          {{ item.lastResult || '-' }}
                        </span>
                        <span class="text-muted font-small-2">({{ item.total }} {{ $t("Total") }})</span>
                      </div>
                      <div class="text-right font-small-2">
                        <span class="font-weight-bolder text-coaching">
                          {{ item.offeredCoachingPerc != null ? item.offeredCoachingPerc : 0 }}%
                        </span>
                        <span class="mx-50 text-muted">|</span>
                        <span class="font-weight-bolder text-pvt-college">
                          {{ item.offeredPvtCollegePerc != null ? item.offeredPvtCollegePerc : 0 }}%
                        </span>
                      </div>
                    </div>

                    <div
                      class="progress"
                      style="height: 10px; border-radius: 6px; background-color: #f0f0f5; overflow: hidden;"
                    >
                      <div
                        class="progress-bar bg-coaching"
                        role="progressbar"
                        :style="{ width: (item.offeredCoachingPerc || 0) + '%' }"
                        :aria-valuenow="item.offeredCoachingPerc || 0"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        v-b-tooltip.hover
                        :title="`${$t('Offered Coaching')}: ${item.offeredCoachingPerc || 0}%`"
                      ></div>
                      <div
                        class="progress-bar bg-pvt-college"
                        role="progressbar"
                        :style="{ width: (item.offeredPvtCollegePerc || 0) + '%' }"
                        :aria-valuenow="item.offeredPvtCollegePerc || 0"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        v-b-tooltip.hover
                        :title="`${$t('Offered Pvt College')}: ${item.offeredPvtCollegePerc || 0}%`"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
          </b-col>
        </b-row>
      </b-card-body>
      <b-table
        class=""
        sticky-header="350px"
        show-empty
        :items="scholarshipItems"
        :fields="scholarshipFields"
        responsive
        hover
        :busy="scholarshipLoading"
      >
        <template #table-busy>
          <div class="d-flex justify-content-center mb-3 mt-3">
            <b-spinner
              style="width: 3rem; height: 3rem"
              type="grow"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </div>
        </template>

        <template #head(accepted)="data">
          <div class="text-center">
            {{ data.label }}
            <div class="font-small-1 text-center">
              {{ $t("Total | App | Admin") }}
            </div>
          </div>
        </template>

        <template #head(notAccepted)="data">
          <div class="text-center">
            {{ data.label }}
            <div class="font-small-1 text-center">
              {{ $t("Total | N/A | Left") }}
            </div>
          </div>
        </template>

        <template #head(status)="data">
          <div class="text-center">
            {{ data.label }}
            <div class="font-small-1 text-center">
              {{ $t("active | probation | inactive") }}
            </div>
          </div>
        </template>

        <template #cell(campus)="data">
          <b-badge variant="light-primary">
            {{ data.item.campusName }}
          </b-badge>
        </template>
        <template #cell(assigned)="data">
          <b-badge variant="light-primary">
            {{ data.item.assigned }}
          </b-badge>
        </template>
        <template #cell(accepted)="data">
          <div class="text-center">
            <b-badge variant="light-success">
              {{ data.item.accepted }}
            </b-badge>
            |
            <b-badge variant="light-warning" class="mr-25">
              {{ data.item.acceptedAppLogin }}
            </b-badge>
            |
            <b-badge variant="light-info">
              {{ data.item.acceptedAdminPortal }}
            </b-badge>
          </div>
        </template>
        <template #cell(notAccepted)="data">
          <div class="text-center">
            <b-badge variant="light-success">
              {{ data.item.notAccepted }}
            </b-badge>
            |
            <b-badge variant="light-warning" class="mr-25">
              {{ data.item.notAccepted - data.item.notAcceptedLeft }}
            </b-badge>
            |
            <b-badge variant="light-danger">
              {{ data.item.notAcceptedLeft }}
            </b-badge>
          </div>
        </template>
        <template #cell(status)="data">
          <div class="text-center">
            <b-badge variant="light-success">
              {{ data.item.active }}
            </b-badge>
            |
            <b-badge variant="light-warning" class="mr-25">
              {{ data.item.onProbation }}
            </b-badge>
            |
            <b-badge variant="light-danger">
              {{ data.item.inactive }}
            </b-badge>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="outline-primary"
            class="btn-icon"
            @click="openScholarshipDetails(data.item)"
          >
            <feather-icon icon="EyeIcon" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- other campuses -->
    <ScholarshipCard
      v-else
      :campusID="selectedCampusId"
      :isHeadOffice="isHeadOffice"
      :scholarshipCategoryOptions="scholarshipCategoryOptions"
      :regionOptions="regionOptions"
      :region="selectedRegion"
      :category="selectedCategory"
      :tabValue="tabValue"
    />

    <!-- Scholarship Filter Modal -->
    <b-modal
      :id="filterModalId"
      :title="$t('Scholarship Filters')"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <b-form-group v-if="!isPcp && !isUtp" :label="$t('Scholarship Category')">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedCategory"
          :options="scholarshipCategoryOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          :placeholder="$t('Select scholarship category')"
        />
      </b-form-group>
      <b-form-group :label="$t('Region')">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedRegion"
          :options="regionOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          :placeholder="$t('Select region')"
        />
      </b-form-group>
      <b-form-group :label="$t('Fiscal Year')" v-if="isUtp">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedFiscalYear"
          :options="utpFiscalOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          :placeholder="$t('Select fiscal year')"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="$bvModal.hide(filterModalId)"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="
            scholarshipLoading || scholarshipPrinting || scholarshipExporting
          "
          @click="
            LoadScholarshipStatistics();
            LoadOtherStats();
            LoadCategoryStats();
            $bvModal.hide(filterModalId);
          "
        >
          {{ $t("Apply") }}
        </b-button>
      </div>
    </b-modal>

    <!-- Scholarship Details Modal -->
    <b-modal
      :id="detailsModalId"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      body-class="p-0"
      no-close-on-backdrop
    >
      <ScholarshipCard
        :campusID="selectedCampusId"
        :isHeadOffice="isHeadOffice"
        :region="selectedRegion"
        :category="selectedCategory"
        :tabValue="tabValue"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BTable,
  BBadge,
  BCardHeader,
  BCardBody,
  BFormGroup,
  BProgress,
  BProgressBar,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import * as XLSX from "xlsx";
import { mapActions } from "vuex";
import ScholarshipCard from "./ScholarshipCard.vue";

export default {
  name: "ScholarshipOverview",
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BCardHeader,
    BCardBody,
    BFormGroup,
    BProgress,
    BProgressBar,
    vSelect,
    ScholarshipCard,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    campusID: {
      type: Number,
      default: 0,
    },
    isHeadOffice: {
      type: Boolean,
      default: false,
    },
    tabValue: {
      type: String,
      default: "none",
    },
  },
  data() {
    const isPcp = String(this.tabValue || "").toLowerCase() === "pcp";
    return {
      selectedCampusId: this.campusID || this.$store?.state?.userData?.cId,
      modalTitle: "",
      scholarshipTotals: {
        assigned: 0,
        accepted: 0,
        acceptedAppLogin: 0,
        acceptedAdminPortal: 0,
        acceptedPerc: 0,
        notAccepted: 0,
        notAcceptedLeft: 0,
        active: 0,
        onProbation: 0,
        inactive: 0,
      },
      scholarshipItems: [],
      assignedStrength: [],
      acceptedStrength: [],
      notAcceptedStrength: [],
      scholarshipFields: [
        { label: "campus", key: "campus" },
        { label: "assigned", key: "assigned", tdClass: "text-center" },
        { label: "accepted", key: "accepted" },
        { label: "not accepted", key: "notAccepted", tdClass: "text-center" },
        { label: "status", key: "status" },
        { label: "actions", key: "actions" },
      ],
      scholarshipLoading: false,
      scholarshipStudents: [],
      scholarshipPrinting: false,
      scholarshipExporting: false,
      otherLoading: false,
      classStats: [],
      genderStats: {
        assignedTotal: 0,
        assignedMale: 0,
        assignedFemale: 0,
        assignedMalePerc: 0,
        assignedFemalePerc: 0,
        acceptedTotal: 0,
        acceptedMale: 0,
        acceptedFemale: 0,
        acceptedMalePerc: 0,
        acceptedFemalePerc: 0,
        notAcceptedTotal: 0,
        notAcceptedMale: 0,
        notAcceptedFemale: 0,
        notAcceptedMalePerc: 0,
        notAcceptedFemalePerc: 0,
      },
      selectedRegion: [],
      selectedCategory: isPcp ? ["Offered PCP"] : [],
      regionOptions: [],
      scholarshipCategoryOptions: [
        "Offered Coaching",
        "Offered Pvt College",
        "Offered TCF College",
      ],
      selectedFiscalYear: [],
      utpFiscalOptions: ["2026-27", "2025-26", "2024-25", "2023-24"],
      catLoading: false,
      lastResultSummary: [],
    };
  },
  computed: {
    isPcp() {
      return String(this.tabValue || "").toLowerCase() === "pcp";
    },
    isUtp() {
      return String(this.tabValue || "").toLowerCase() === "utp";
    },
    filterModalId() {
      return `modal-scholarship-filter-${this.tabValue || "none"}`;
    },
    detailsModalId() {
      return `modal-scholarship-${this.tabValue || "none"}`;
    },
    remainingNotAccepted() {
      return (
        this.scholarshipTotals.notAccepted -
        this.scholarshipTotals.notAcceptedLeft
      );
    },
    facultyStatsDistinctClasses() {
      const set = new Set();
      const rows = this.classStats || [];
      for (const row of rows) {
        const arr = row.classes;
        if (!Array.isArray(arr)) continue;
        for (const b of arr) {
          if (b != null && b.className != null) set.add(b.className);
        }
      }
      return [...set].sort((a, b) => a - b);
    },
    facultyStatsFields() {
      const thClass = "bg-success text-white";
      const fields = [{ key: "section", label: "faculty", thClass }];

      for (const mid of this.facultyStatsDistinctClasses) {
        fields.push({ key: `mdb_${mid}`, label: mid, thClass });
      }
      return fields;
    },
  },
  watch: {
    campusID(newVal) {
      if (newVal) {
        this.selectedCampusId = newVal;
      }
    },
    isHeadOffice(newVal) {
      if (newVal) {
        this.LoadScholarshipStatistics();
        this.LoadOtherStats();
        this.LoadCategoryStats();
      }
    },
    tabValue(newVal) {
      if (String(newVal || "").toLowerCase() === "pcp") {
        this.selectedCategory = ["Offered PCP"];
      } else if (
        this.selectedCategory.length === 1 &&
        this.selectedCategory[0] === "Offered PCP"
      ) {
        this.selectedCategory = [];
      }
      if (this.isHeadOffice) {
        this.LoadScholarshipStatistics();
        this.LoadOtherStats();
        this.LoadCategoryStats();
      }
    },
  },
  created() {
    if (this.campusID) {
      this.selectedCampusId = this.campusID;
    } else if (this.$store?.state?.userData?.cId) {
      this.selectedCampusId = this.$store.state.userData.cId;
    }
    if (this.isPcp) {
      this.selectedCategory = ["Offered PCP"];
    }
    this.LoadRegions();
    if (this.isHeadOffice) {
      this.LoadScholarshipStatistics();
      this.LoadOtherStats();
      this.LoadCategoryStats()
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async printScholarshipSummary(isExport) {
      this.scholarshipPrinting = !isExport;
      this.scholarshipExporting = isExport;
      this.scholarshipStudents = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/ScholarshipSummaryAll?db=" +
          this.$store.state.userData.db +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || "") +
          "&fiscalYear=" +
          (this.selectedFiscalYear.join(",") || "") +
          "&isUtp=" +
          (this.isUtp || false),
        token: this.$store.state.userData.token,
      });
      if (isExport) this.exportScholarshipReport();
      else this.printScholarshipReport();

      this.scholarshipPrinting = false;
      this.scholarshipExporting = false;
    },
    escapeHtml(text) {
      if (text == null) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    statusVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "active") return "success";
      if (s === "in-active" || s === "inactive") return "danger";
      if (s === "on-probation") return "warning";
      return "secondary";
    },
    scholarshipVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "accepted") return "success";
      if (s === "not-accepted" || s === "not accepted") return "danger";
      if (s === "pending") return "warning";
      return "secondary";
    },
    async printScholarshipReport() {
      const campusMeta = this.$store.state.currentBranch || {};
      const displayName = campusMeta.name || "";
      const branch = campusMeta.branch || "";

      const schoolUpper = displayName.toUpperCase();
      const campusUpper = branch.toUpperCase();

      const now = new Date();
      const periodStr = now.toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const generatedStr = now.toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const reportTitle = this.$t("Scholarship Status Report");
      const recordCount = (this.scholarshipStudents || []).length;

      const mainTitleHtml = campusUpper
        ? `<span class="school-name">${this.escapeHtml(
            schoolUpper
          )}</span><span class="title-dash"> — </span><span class="campus-name">${this.escapeHtml(
            campusUpper
          )}</span>`
        : `<span class="school-name">${this.escapeHtml(schoolUpper)}</span>`;

      const metaHtml = `${this.escapeHtml(
        reportTitle
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Period"
      )}:</strong> ${this.escapeHtml(
        periodStr
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Records"
      )}:</strong> ${recordCount} <span class="meta-sep">|</span> <strong>${this.$t(
        "Generated on"
      )}</strong> ${this.escapeHtml(generatedStr)}`;

      const thead = `<tr>
        <th>${this.$t("Federal Board Reg No")}</th>
        <th>${this.$t("Student Name")}</th>
        <th>${this.$t("Father Name")}</th>
        <th>${this.$t("Institute")}</th>
        <th>${this.$t("Class")}</th>
        <th>${this.$t("Scholarship Category")}</th>
        <th>${this.$t("Scholarship Status")}</th>
        <th>${this.$t("Student Status")}</th>
        <th>WhatsApp No</th>
        <th>Parent's Contact</th>
        <th>Student's Contact</th>
      </tr>`;

      const rows = (this.scholarshipStudents || [])
        .map((item) => {
          const camp = item.campus || {};
          const st = item.student || {};
          const det = item.studentDetails || {};
          const schV = this.scholarshipVariant(det.scholarshipStatus || "");
          const stV = this.statusVariant(det.studentStatus || "");
          const fed = det.federalRegNo ? String(det.federalRegNo) : "—";
          const wa =
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "—";
          const parentC =
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "—";
          const studentC =
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "—";
          return `<tr>
            <td>${this.escapeHtml(fed)}</td>
            <td>${this.escapeHtml(st.name || "—")}</td>
            <td>${this.escapeHtml(st.father_name || "—")}</td>
            <td>${this.escapeHtml(camp.name || "—")}</td>
            <td>${this.escapeHtml(st.meta_class || "—")}</td>
            <td>${this.escapeHtml(det.scholarshipCategory || "—")}</td>
            <td class="pv-hl-${schV}">${this.escapeHtml(
            det.scholarshipStatus || "—"
          )}</td>
            <td class="pv-hl-${stV}">${this.escapeHtml(
            det.studentStatus || "—"
          )}</td>
            <td>${this.escapeHtml(wa)}</td>
            <td>${this.escapeHtml(parentC)}</td>
            <td>${this.escapeHtml(studentC)}</td>
          </tr>`;
        })
        .join("");

      const emptyRow = `<tr><td colspan="9" class="text-center">${this.$t(
        "No records"
      )}</td></tr>`;

      const docTitle = this.$t("Scholarship Summary");

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${this.escapeHtml(
        docTitle
      )}</title>
<style>
  @page { size: A4 landscape; margin: 4mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  .print-header { margin-bottom: 0; }
  .print-main-title {
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.25;
    margin: 0 0 10px;
  }
  .print-main-title .school-name { color: #000; }
  .print-main-title .title-dash { color: #000; font-weight: 700; }
  .print-main-title .campus-name { color: #4a4a4a; font-weight: 700; }
  .print-meta {
    font-size: 11px;
    color: #5c5c5c;
    font-weight: 500;
    line-height: 1.5;
    margin: 0 0 10px;
  }
  .print-meta .meta-sep {
    color: #9a9a9a;
    font-weight: 300;
    padding: 0 10px;
  }
  .print-meta strong { color: #444; font-weight: 600; }
  .print-header-rule {
    border: none;
    border-bottom: 3px solid #000;
    margin: 0 0 14px;
    padding: 0;
    height: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #e8e8e8; font-weight: 600; }
  tr:nth-child(even) td:not([class*="pv-hl-"]) { background: #fafafa; }
  td[class*="pv-hl-"] {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-weight: 600;
  }
  td.pv-hl-success { background: #cfe9de !important; color: #0f5132 !important; }
  td.pv-hl-danger { background: #f8d7da !important; color: #842029 !important; }
  td.pv-hl-warning { background: #fff3cd !important; color: #664d03 !important; }
  td.pv-hl-secondary { background: #e9ecef !important; color: #495057 !important; }
  .text-center { text-align: center; }
</style></head><body>
  <header class="print-header">
    <div class="print-main-title">${mainTitleHtml}</div>
    <div class="print-meta">${metaHtml}</div>
    <hr class="print-header-rule" />
  </header>
  <table><thead>${thead}</thead><tbody>${rows || emptyRow}</tbody></table>
</body></html>`;

      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "style",
        "position:fixed;width:0;height:0;border:0;visibility:hidden;"
      );
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => iframe.remove(), 400);
        }
      }, 200);
    },
    exportScholarshipReport() {
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = `scholarship-summary-${stamp}.xlsx`;

      const kFederal = this.$t("Federal Board Reg No");
      const kStudent = this.$t("Student Name");
      const kFather = this.$t("Father Name");
      const kCampus = this.$t("Institute");
      const kClass = this.$t("Class");
      const kSchCat = this.$t("Scholarship Category");
      const kSch = this.$t("Scholarship Status");
      const kStat = this.$t("Student Status");
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";
      const kStatus = "Current Status";

      const expData = (this.scholarshipStudents || []).map((item) => {
        const camp = item.campus || {};
        const st = item.student || {};
        const det = item.studentDetails || {};
        return {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kCampus]: camp.name || "",
          [kClass]: st.meta_class || "",
          [kSchCat]: det.scholarshipCategory || "",
          [kSch]: det.scholarshipStatus || "",
          [kStat]: det.studentStatus || "",
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
          [kStatus]: st.status || "",
          "Accepted By": det.acceptedBy || "",
          "Verified By": det.verifiedBy || "",
          ...(this.isUtp
            ? {
                "UTP Support": det.utpSupport || "",
                "UTP Enrolled On": det.utpEnrolledOn
                  ? new Date(det.utpEnrolledOn).toLocaleDateString("en-UK", {
                      year: "numeric",
                      day: "numeric",
                      month: "short",
                    })
                  : "",
                "UTP Fiscal Year": det.utpFiscalYear || "",
                "UTP Course": det.utpCourse || "",
                "1. Locked Program": det.utpLockedProgram1 || "",
                "1. Locked Tier": det.utpLockedTier1 || "",
                "2. Locked Program": det.utpLockedProgram2 || "",
                "2. Locked Tier": det.utpLockedTier2 || "",
                // "3. Locked Program": det.utpLockedProgram3 || "",
                // "3. Locked Tier": det.utpLockedTier3 || "",
                "UTP Institute ID": det.utpInstituteID || "",
                "UTP Institute": det.utpInstituteName || "",
                "Mode of Class": det.utpClassMode || "",
                "Qarz-e-hasna Form Status": det.utpQarzehasnaStatus || "",
                "UTP Status": det.utpStatus || "",
                "UTP Comment": det.utpComment || "",
              }
            : {}),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Scholarship");
      XLSX.writeFile(workbook, fname);
    },

    async LoadScholarshipStatistics() {
      this.scholarshipLoading = true;
      const url = this.isPcp
        ? this.$store.state.domain +
          "StudentDetails/ScholarshipSummaryPCP?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&region=" +
          (this.selectedRegion.join(",") || "")
        : this.isUtp
        ? this.$store.state.domain +
          "StudentDetails/UTPScholarshipSummary?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&fiscalYear=" +
          (this.selectedFiscalYear.join(",") || "")
        : this.$store.state.domain +
          "StudentDetails/ScholarshipSummaryByBranch?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || "");

      let res = await this.get({
        url,
        token: this.$store.state.userData.token,
      });
      this.scholarshipLoading = false;
      this.scholarshipItems = res?.branches ?? [];
      if (res?.totalCounts) this.scholarshipTotals = res?.totalCounts;
      // this.assignedStrength = res?.classStrength ?? [];
      // this.acceptedStrength = res?.classAcceptedStrength ?? [];
      // this.notAcceptedStrength = res?.classNotAcceptedStrength ?? [];
    },
    async LoadOtherStats() {
      this.otherLoading = true;

      const url =  this.isUtp ?  
      this.$store.state.domain +
          "StudentDetails/UTPScholarshipStats?db=" +
          this.$store.state.userData.db +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&fiscalYear=" +
          (this.selectedFiscalYear.join(",") || "")
        :
       this.$store.state.domain +
          "StudentDetails/ScholarshipStats?db=" +
          this.$store.state.userData.db +
          "&region=" +
          (this.selectedRegion.join(",") || "") +
          "&category=" +
          (this.selectedCategory.join(",") || "") +
          "&isUtp=false";

      let res = await this.get({
        url,
        token: this.$store.state.userData.token,
      });
      this.otherLoading = false;
      if (res) {
        if (res.genderStats) this.genderStats = res.genderStats;
        this.classStats = res?.classStats ?? [];
        this.assignedStrength = res?.classStrength ?? [];
        this.acceptedStrength = res?.classAcceptedStrength ?? [];
        this.notAcceptedStrength = res?.classNotAcceptedStrength ?? [];
      }
    },
    async LoadCategoryStats() {
      if (!this.isPcp && !this.isUtp){
        this.catLoading = true;
  
        const url = this.$store.state.domain +
            "StudentDetails/CategorySummary?db=" +
            this.$store.state.userData.db +
            "&region=" +
            (this.selectedRegion.join(",") || "") +
            "&fiscalYear=" +
            (this.selectedFiscalYear.join(",") || "");
  
        let res = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        // console.log(res);
        this.lastResultSummary = Array.isArray(res.lastResultSummary) ? res.lastResultSummary : []
        this.catLoading = false;
      }
    },
    async LoadRegions() {
      let res = await this.get({
        url:
          this.$store.state.domain +
          "StudentDetails/GetRegions?db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
      });
      this.regionOptions = res;
    },
    getFacultyStatsForClass(row, cls) {
      const arr = row && row.classes;
      if (!Array.isArray(arr)) return null;
      return arr.find((b) => b != null && b.className === cls) || null;
    },
    openScholarshipDetails(item) {
      this.selectedCampusId = item.campusID;
      this.modalTitle = item.campusName;
      this.$bvModal.show(this.detailsModalId);
    },
  },
};
</script>

<style lang="scss" scoped>
.scholarship-skeleton-loader {
  background: linear-gradient(90deg, #f5f6fa 25%, #e9ecef 50%, #f5f6fa 75%);
  background-size: 200% 100%;
  animation: shimmerNew 1.5s infinite;
  border-radius: 8px;
  height: 60px;
}

@keyframes shimmerNew {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* --- Scholarship Stats Card Redesign --- */
.scholarship-card-new {
  background-color: #ffffff;
  border: 1.5px solid #f0f0f5;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0px 5px 4px 1px rgba(34, 41, 47, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100% - 1rem);
}

.scholarship-card-new:hover {
  transform: translateY(-5px);
}

.scholarship-card-new.border-top-primary {
  border-top: 4px solid #7367f0 !important;
}

.scholarship-card-new.border-top-success {
  border-top: 4px solid #28c76f !important;
}

.scholarship-card-new.border-top-warning {
  border-top: 4px solid #ff9f43 !important;
}

.scholarship-card-new.border-top-info {
  border-top: 4px solid #00cfe8 !important;
}

.scholarship-card-icon-container {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.scholarship-card-icon-container:hover {
  transform: scale(1.1);
}

.scholarship-card-icon-container.bg-light-primary {
  background-color: #f3f2fe;
  color: #7367f0;
}

.scholarship-card-icon-container.bg-light-success {
  background-color: #e8f9f0;
  color: #28c76f;
}

.scholarship-card-icon-container.bg-light-warning {
  background-color: #fff3e8;
  color: #ff9f43;
}

.scholarship-card-icon-container.bg-light-info {
  background-color: #e0f9fc;
  color: #00cfe8;
}

.scholarship-card-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #82868b;
  text-transform: uppercase;
}

.scholarship-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c2738;
  margin-top: 0.15rem;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.scholarship-card-divider {
  border: 0;
  border-top: 1.5px solid #f0f0f5;
  margin: 0.5rem 0;
}

.scholarship-card-table {
  box-shadow: 0px 5px 4px 1px rgba(34, 41, 47, 0.3) !important;
}

.bullet-coaching {
  background-color: #8acfb3 !important;
}

.text-coaching {
  color: #8acfb3 !important;
}

.bg-coaching {
  background-color: #8acfb3 !important;
}

.bullet-pvt-college {
  background-color: #6993b8 !important;
}

.text-pvt-college {
  color: #6993b8 !important;
}

.bg-pvt-college {
  background-color: #6993b8 !important;
}
</style>
